import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, PlusCircle, Trash2 } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody, CardHeader } from '@/components/Card';
import { Skeleton, SkeletonRegion } from '@/components/Skeleton';
import {
  atualizarServico,
  chavesCatalogo,
  criarServico,
  excluirServico,
  listarServicos,
  type Criticality,
  type PortfolioStage,
  type ServiceCatalogItem,
  type ServiceType,
} from '@/features/catalog/catalog.api';

const TIPO_PT: Record<ServiceType, string> = {
  BUSINESS: 'Ao cliente',
  TECHNICAL: 'Interno',
};

const STAGE_PT: Record<PortfolioStage, string> = {
  PIPELINE: 'Pipeline',
  CATALOGUE: 'Catálogo',
  RETIRED: 'Aposentado',
};

const STAGE_COR: Record<PortfolioStage, string> = {
  PIPELINE: 'bg-primary/10 text-primary ring-1 ring-inset ring-primary/20',
  CATALOGUE: 'bg-success/12 text-success ring-1 ring-inset ring-success/25',
  RETIRED: 'bg-muted text-muted-foreground ring-1 ring-inset ring-border',
};

const CRITICIDADE_COR: Record<Criticality, string> = {
  CRITICAL: 'text-destructive font-semibold',
  HIGH: 'text-warning font-medium',
  MEDIUM: 'text-foreground',
  LOW: 'text-muted-foreground',
};

const CRITICIDADE_PT: Record<Criticality, string> = {
  CRITICAL: 'Crítico',
  HIGH: 'Alto',
  MEDIUM: 'Médio',
  LOW: 'Baixo',
};

const TIPO_OPTIONS: ServiceType[] = ['BUSINESS', 'TECHNICAL'];
const STAGE_OPTIONS: PortfolioStage[] = ['PIPELINE', 'CATALOGUE', 'RETIRED'];
const CRIT_OPTIONS: Criticality[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

function NovoServicoForm({ onCancel }: { onCancel: () => void }) {
  const qc = useQueryClient();
  const [key, setKey] = useState('');
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [tipo, setTipo] = useState<ServiceType>('BUSINESS');
  const [stage, setStage] = useState<PortfolioStage>('CATALOGUE');
  const [criticidade, setCriticidade] = useState<Criticality>('MEDIUM');

  const criar = useMutation({
    mutationFn: () => criarServico({ key, name: nome, description: descricao || undefined, serviceType: tipo, portfolioStage: stage, criticality: criticidade }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['catalogo'] });
      onCancel();
    },
  });

  return (
    <Card>
      <CardHeader title="Novo serviço" />
      <CardBody>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Chave (único)</label>
            <input
              className="w-full rounded-lg border border-input/80 bg-muted/40 px-3 py-2 text-sm transition-colors placeholder:text-muted-foreground hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow"
              placeholder="ex: email-corporativo"
              value={key}
              onChange={(e) => setKey(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Nome</label>
            <input
              className="w-full rounded-lg border border-input/80 bg-muted/40 px-3 py-2 text-sm transition-colors placeholder:text-muted-foreground hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow"
              placeholder="Nome do serviço"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-muted-foreground mb-1">Descrição</label>
            <textarea
              className="w-full rounded-lg border border-input/80 bg-muted/40 px-3 py-2 text-sm transition-colors placeholder:text-muted-foreground hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow"
              rows={2}
              placeholder="Descrição breve do serviço"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Tipo</label>
            <select
              className="w-full rounded-lg border border-input/80 bg-muted/40 px-3 py-2 text-sm transition-colors placeholder:text-muted-foreground hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow"
              value={tipo}
              onChange={(e) => setTipo(e.target.value as ServiceType)}
            >
              {TIPO_OPTIONS.map((t) => <option key={t} value={t}>{TIPO_PT[t]}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Estágio</label>
            <select
              className="w-full rounded-lg border border-input/80 bg-muted/40 px-3 py-2 text-sm transition-colors placeholder:text-muted-foreground hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow"
              value={stage}
              onChange={(e) => setStage(e.target.value as PortfolioStage)}
            >
              {STAGE_OPTIONS.map((s) => <option key={s} value={s}>{STAGE_PT[s]}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Criticidade</label>
            <select
              className="w-full rounded-lg border border-input/80 bg-muted/40 px-3 py-2 text-sm transition-colors placeholder:text-muted-foreground hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow"
              value={criticidade}
              onChange={(e) => setCriticidade(e.target.value as Criticality)}
            >
              {CRIT_OPTIONS.map((c) => <option key={c} value={c}>{CRITICIDADE_PT[c]}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancelar</Button>
          <Button
            size="sm"
            disabled={!key.trim() || !nome.trim() || criar.isPending}
            loading={criar.isPending}
            onClick={() => criar.mutate()}
          >
            Criar serviço
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function ServicoCard({ servico }: { servico: ServiceCatalogItem }) {
  const qc = useQueryClient();
  const [expandido, setExpandido] = useState(false);
  const [editandoStage, setEditandoStage] = useState(false);

  const atualizar = useMutation({
    mutationFn: (dto: Parameters<typeof atualizarServico>[1]) => atualizarServico(servico.id, dto),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['catalogo'] }),
  });

  const excluir = useMutation({
    mutationFn: () => excluirServico(servico.id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['catalogo'] }),
  });

  return (
    <Card>
      <CardBody>
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <button
              onClick={() => setExpandido((v) => !v)}
              className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
              aria-label={expandido ? 'Recolher' : 'Expandir'}
            >
              {expandido
                ? <ChevronDown className="h-4 w-4" aria-hidden />
                : <ChevronRight className="h-4 w-4" aria-hidden />}
            </button>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-foreground">{servico.name}</span>
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">{servico.key}</code>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STAGE_COR[servico.portfolioStage]}`}>
                  {STAGE_PT[servico.portfolioStage]}
                </span>
                {!servico.active && (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">Inativo</span>
                )}
              </div>
              {servico.description && (
                <p className="mt-1 text-sm text-muted-foreground">{servico.description}</p>
              )}
              <p className="mt-1 text-xs text-muted-foreground">
                Criticidade:{' '}
                <span className={CRITICIDADE_COR[servico.criticality]}>
                  {CRITICIDADE_PT[servico.criticality]}
                </span>
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {editandoStage ? (
              <select
                className="rounded-lg border border-input/80 bg-muted/40 px-2 py-1 text-xs transition-colors hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow"
                value={servico.portfolioStage}
                autoFocus
                onBlur={() => setEditandoStage(false)}
                onChange={(e) => {
                  atualizar.mutate({ portfolioStage: e.target.value as PortfolioStage });
                  setEditandoStage(false);
                }}
              >
                {STAGE_OPTIONS.map((s) => <option key={s} value={s}>{STAGE_PT[s]}</option>)}
              </select>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => setEditandoStage(true)}>
                Mover
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (confirm(`Arquivar "${servico.name}"?`)) excluir.mutate();
              }}
              disabled={excluir.isPending}
              title="Arquivar serviço"
            >
              <Trash2 className="h-4 w-4 text-destructive" aria-hidden />
            </Button>
          </div>
        </div>
        {expandido && (
          <div className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground">
            <p>Tipo: <strong>{TIPO_PT[servico.serviceType]}</strong></p>
            <p className="mt-1">Criado em: {new Date(servico.createdAt).toLocaleDateString('pt-BR')}</p>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

type FiltroTipo = ServiceType | 'TODOS';

export function CatalogoPage() {
  const [filtroTipo, setFiltroTipo] = useState<FiltroTipo>('TODOS');
  const [filtroStage, setFiltroStage] = useState<PortfolioStage | undefined>(undefined);
  const [criando, setCriando] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: chavesCatalogo.lista(
      filtroTipo === 'TODOS' ? undefined : filtroTipo,
      filtroStage,
    ),
    queryFn: () => listarServicos(
      filtroTipo === 'TODOS' ? undefined : filtroTipo,
      filtroStage,
    ),
  });

  const servicos = data ?? [];
  const business = servicos.filter((s) => s.serviceType === 'BUSINESS');
  const technical = servicos.filter((s) => s.serviceType === 'TECHNICAL');

  const abasTipo: Array<{ valor: FiltroTipo; label: string }> = [
    { valor: 'TODOS', label: 'Todos' },
    { valor: 'BUSINESS', label: 'Ao cliente' },
    { valor: 'TECHNICAL', label: 'Interno' },
  ];

  return (
    <div className="mx-auto max-w-[1400px] space-y-3">
      <header className="flex items-center justify-between gap-4 border-b border-border pb-3">
        <h1 className="text-base font-semibold">Catálogo de Serviços</h1>
        <Button onClick={() => setCriando(true)} disabled={criando} icon={<PlusCircle className="h-4 w-4" aria-hidden />}>
          Novo serviço
        </Button>
      </header>

      {criando && <NovoServicoForm onCancel={() => setCriando(false)} />}

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-border overflow-hidden">
          {abasTipo.map(({ valor, label }) => (
            <button
              key={valor}
              onClick={() => setFiltroTipo(valor)}
              className={`px-3 py-1.5 text-sm transition-colors ${
                filtroTipo === valor
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card text-muted-foreground hover:bg-accent'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <select
          className="rounded-lg border border-input/80 bg-muted/40 px-3 py-1.5 text-sm transition-colors hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow"
          value={filtroStage ?? ''}
          onChange={(e) => setFiltroStage((e.target.value as PortfolioStage) || undefined)}
        >
          <option value="">Todos os estágios</option>
          {STAGE_OPTIONS.map((s) => <option key={s} value={s}>{STAGE_PT[s]}</option>)}
        </select>
      </div>

      {isPending ? (
        <SkeletonRegion>
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full rounded-lg" />)}
          </div>
        </SkeletonRegion>
      ) : filtroTipo === 'TODOS' ? (
          <div className="space-y-6">
            {business.length > 0 && (
              <section>
                <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Ao cliente ({business.length})
                </h2>
                <div className="space-y-2">
                  {business.map((s) => <ServicoCard key={s.id} servico={s} />)}
                </div>
              </section>
            )}
            {technical.length > 0 && (
              <section>
                <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Interno ({technical.length})
                </h2>
                <div className="space-y-2">
                  {technical.map((s) => <ServicoCard key={s.id} servico={s} />)}
                </div>
              </section>
            )}
            {servicos.length === 0 && (
              <div className="py-12 text-center text-muted-foreground">
                Nenhum serviço cadastrado.
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {servicos.length === 0
              ? <div className="py-12 text-center text-muted-foreground">Nenhum serviço encontrado.</div>
              : servicos.map((s) => <ServicoCard key={s.id} servico={s} />)
            }
          </div>
        )}
    </div>
  );
}
